import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { configApiRef, identityApiRef } from '@backstage/core-plugin-api';

interface CompanyOption {
  value: string;
  label: string;
}

const useBillingCompaniesPicker = () => {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const [options, setOptions] = useState<CompanyOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchCompanies() {
      setLoading(true);
      setError(null);

      try {
        const baseUrl = configApi.getString('backend.baseUrl');
        const { token } = await identityApi.getCredentials();

        const response = await fetch(
          `${baseUrl}/api/billing-portal/proxy/companies.get`,
          {
            method: 'POST',
            headers: token
              ? {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                }
              : {
                  'Content-Type': 'application/json',
                },
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch companies: ${response.statusText}`);
        }

        const data = await response.json();

        // Assuming the API returns an array of companies with id and name fields
        const companyOptions: CompanyOption[] = (data || []).map(
          (company: any) => ({
            value: company.slug,
            label: company.name,
          }),
        );
        companyOptions.sort((a, b) => a.label.localeCompare(b.label));

        setOptions(companyOptions);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to fetch companies',
        );
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }

    fetchCompanies();
  }, [configApi, identityApi]);

  return { options, loading, error };
};

export default useBillingCompaniesPicker;
